<script setup>
import DeleteTaskTimeAction from "@frontJs/components/actions/projectManagement/tasks/DeleteTaskTimeAction.vue";
import { usePage } from "@inertiajs/vue3";
import ActionButton from "@js/components/ActionButton.vue";
import Drawer from "@js/components/Drawer.vue";
import InputField from "@js/components/fields/InputField.vue";
import ListboxField from "@js/components/fields/ListboxField.vue";
import TextareaField from "@js/components/fields/TextareaField.vue";
import TimeField from "@js/components/fields/TimeField.vue";
import { useRoute } from "@js/utils/route";
import { useChangeCase } from "@vueuse/integrations/useChangeCase";
import { Form } from "vee-validate";
import { computed, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import { object, string } from "yup";

const { t } = useI18n();

const types = [
  { value: "hourly", label: t("label.hourly") },
  { value: "package", label: t("label.package") },
  { value: "project", label: t("label.project") },
];

const themes = [
  { value: "seo", label: t("label.seo") },
  { value: "sea", label: t("label.sea") },
  { value: "smo", label: t("label.smo") },
  { value: "sma", label: t("label.sma") },
  { value: "sem", label: t("label.sem") },
  { value: "dev", label: t("label.dev") },
  { value: "dev_seo", label: t("label.devSeo") },
  { value: "cs", label: t("label.cs") },
];

const props = defineProps({
  task: {
    type: Object,
    required: true,
  },
  taskTime: {
    type: Object,
    required: false,
  },
  callback: {
    type: Function,
    required: false,
  },
});

const { route } = useRoute();

const currentUser = computed(() => usePage().props.currentUser);

const form = reactive({
  taskType: types.find((type) => type.value === props.task.type),
  taskTheme: themes.find((theme) => theme.value === props.task.theme),
  taskName: props.task.name,
  date: props.taskTime
    ? props.taskTime.date
    : new Date().toISOString().split("T")[0],
  hours: props.taskTime ? props.taskTime.hours : "",
  minutes: props.taskTime ? props.taskTime.minutes : "",
  user: props.taskTime
    ? props.task.users.find((user) => user.id === props.taskTime.userId) ??
      props.task.users
    : props.task.users.find((user) => user.id === currentUser.value.id) ??
      props.task.users[0],
  description: props.taskTime ? props.taskTime.description : "",
});

const validationSchema = object({
  taskType: object().required(t("error.required")),
  taskTheme: object().required(t("error.required")),
  taskName: string().required(t("error.required")),
  date: string().required(t("error.required")),
  hours: string(),
  minutes: string().required(t("error.required")),
  user: object().required(t("error.required")),
  description: string().nullable(),
});

const isOpen = ref(false);

const formattedTaskNamePrefix = computed(() => {
  let theme = useChangeCase(form.taskTheme.value, "camelCase").value;

  let namePrefix = `[${props.task.tenant.name}] [${t("abbreviation." + form.taskType.value)}] [${t("label." + theme)}]`;

  if (props.task.recurrentType) {
    namePrefix += ` [${t("abbreviation.recurrent")}]`;

    if (props.task.recurrentType === "weekly") {
      namePrefix += ` [${t("abbreviation.onceAWeek")}]`;
    }

    if (props.task.recurrentType === "monthly") {
      namePrefix += ` [${t("abbreviation.onceAMonth")}]`;
    }

    if (props.task.recurrentType === "yearly") {
      namePrefix += ` [${t("abbreviation.onceAYear")}]`;
    }
  }

  namePrefix += ` -`;

  return namePrefix;
});

function setIsOpen(value) {
  isOpen.value = value;
}

function saveTaskTime() {
  let url = props.taskTime
    ? route("front.projectManagement.task.postTaskTime", {
        task: props.task.id,
        taskTime: props.taskTime.id,
      })
    : route("front.projectManagement.task.postTaskTime", {
        task: props.task.id,
      });

  let postData = {
    taskType: form.taskType.value,
    taskTheme: form.taskTheme.value,
    taskName: form.taskName,
    date: form.date,
    hours: form.hours ?? "00",
    minutes: form.minutes,
    user_id: form.user.id,
    description: form.description,
  };

  fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-TOKEN": document
        .querySelector('meta[name="csrf-token"]')
        .getAttribute("content"),
    },
    body: JSON.stringify(postData),
  })
    .then((response) => response.json())
    .then((data) => {
      setIsOpen(false);

      if (props.callback) {
        props.callback();
      }

      if (props.taskTime) {
        form.taskType = types.find((type) => type.value === data.task.type);
        form.taskTheme = themes.find(
          (theme) => theme.value === data.task.theme,
        );
        form.taskName = data.task.name;
        form.date = data.date;
        form.hours = data.hours;
        form.minutes = data.minutes;
        form.user = props.task.users.find((user) => user.id === data.userId);
        form.description = data.description;
      } else {
        form.taskType = types[0];
        form.taskTheme = themes[0];
        form.task.name = "";
        form.date = new Date().toISOString().split("T")[0];
        form.hours = "";
        form.minutes = "";
        form.user =
          props.task.users.find((user) => user.id === currentUser.value.id) ??
          props.task.users[0];
        form.description = "";
      }
    })
    .catch((error) => {
      console.error(error);
    });
}
</script>

<template>
  <slot name="button" :set-is-open="setIsOpen" />

  <Drawer :is-open="isOpen" :set-is-open="setIsOpen" size="large">
    <template #title>
      <div class="flex items-center">
        <template v-if="props.taskTime">
          {{
            t("all.title.updateTaskTime", {
              taskId: props.task.id,
              taskTimeId: props.taskTime.id,
            })
          }}
        </template>
        <template v-else>
          {{ t("all.title.addTaskTime", { taskId: props.task.id }) }}
        </template>
      </div>
    </template>

    <template #full-content>
      <Form
        :validation-schema="validationSchema"
        @submit="saveTaskTime"
        v-slot="{ errors }"
        class="flex h-full flex-col"
      >
        <div class="h-full max-h-[calc(100vh-8rem)] overflow-y-auto px-6 py-4">
          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2">
            <ListboxField
              v-model="form.taskType"
              field-id="taskType"
              field-name="taskType"
              :label="t('label.type')"
              :options="types"
              required
              :disabled="props.task.isChild"
            >
              <template #buttonContent>
                {{ form.taskType.label }}
              </template>

              <template #optionContent="{ option }">
                {{ option.label }}
              </template>
            </ListboxField>

            <ListboxField
              v-model="form.taskTheme"
              field-id="taskTheme"
              field-name="taskTheme"
              :label="t('label.theme')"
              :options="themes"
              required
              :disabled="props.task.isChild"
            >
              <template #buttonContent>
                {{ form.taskTheme.label }}
              </template>

              <template #optionContent="{ option }">
                {{ option.label }}
              </template>
            </ListboxField>
          </div>

          <InputField
            v-model="form.taskName"
            :label="t('all.label.taskName')"
            :placeholder="t('all.label.taskName')"
            field-name="taskName"
            field-id="taskName"
            required
          >
            <template #inputPrefix>
              {{ formattedTaskNamePrefix }}
            </template>
          </InputField>

          <InputField
            v-model="form.date"
            :label="t('label.date')"
            :placeholder="t('label.date')"
            field-name="date"
            field-id="date"
            type="date"
            required
          />

          <TimeField
            v-model:hours="form.hours"
            v-model:minutes="form.minutes"
            hours-field-id="hours"
            hours-field-name="hours"
            :minutes="form.minutes"
            minutes-field-id="minutes"
            minutes-field-name="minutes"
            :label="t('all.label.time')"
            required
          />

          <ListboxField
            v-model="form.user"
            field-id="user"
            field-name="user"
            :label="t('all.label.taskManager')"
            :options="props.task.users"
            required
          >
            <template #buttonContent>
              {{ form.user.fullName }}
            </template>

            <template #optionContent="{ option }">
              {{ option.fullName }}
            </template>
          </ListboxField>

          <TextareaField
            v-model="form.description"
            :label="t('label.description')"
            :placeholder="t('label.description')"
            field-name="description"
            field-id="description"
          />
        </div>

        <div
          class="mt-auto flex flex-wrap items-center gap-2 border-t border-t-gray-200 px-6 py-4"
        >
          <DeleteTaskTimeAction
            v-if="props.taskTime"
            :task-time="props.taskTime"
            :callback="props.callback"
          >
            <template #button="{ setIsOpen }">
              <ActionButton
                type="transparent"
                :callback="() => setIsOpen(true)"
              >
                {{ t("button.delete") }}
              </ActionButton>
            </template>
          </DeleteTaskTimeAction>

          <ActionButton submit :disabled="Object.keys(errors).length > 0">
            {{ t("button.save") }}
          </ActionButton>
        </div>
      </Form>
    </template>
  </Drawer>
</template>
